import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogActions, Button, Typography, Box, IconButton, Autocomplete, TextField, CircularProgress, Chip, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MailIcon from '@mui/icons-material/Mail';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import axios from 'axios';
import config from '../config/config';
import handleOpenSweetAlert from './handleOpenSweetAlert';
import SimpleSuccessSpinner from './LoadingSpinner';
import { useSession } from './hooks/useSession';
import ExcelJS from 'exceljs';

const getEmpresaIdsFromSession = () => {
  try {
    const raw = sessionStorage.getItem('EmpresaId');
    if (!raw) return [];
    const parsed = JSON.parse(raw); // '[2]' -> [2]  or  '[1,2,3]' -> [1,2,3]
    if (Array.isArray(parsed)) return parsed.map(Number).filter(n => n > 0);
    const n = Number(parsed);
    return n > 0 ? [n] : [];
  } catch {
    return [];
  }
};

export default function ModalSolicitarFondos({ open, onClose, selectedRows, onSent, empresaIds }) {
  const apiUrl = config.apiBaseUrl;
  const { loggedInUsername } = useSession();
  
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCCUsers, setSelectedCCUsers] = useState([]);
  const [cuentasPorDivisa, setCuentasPorDivisa] = useState({});
  const [cuentaGlobal, setCuentaGlobal] = useState('');
  const [mismaCuenta, setMismaCuenta] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      // Use empresaIds prop if valid, otherwise fall back to sessionStorage
      let ids = Array.isArray(empresaIds) && empresaIds.length > 0
        ? empresaIds.filter(n => n > 0)
        : getEmpresaIdsFromSession();


      const res = await axios.post(`${apiUrl}/api/usuarios-emails`, { idEmpresa: ids });
      setUsers(res.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, [apiUrl, empresaIds]);

  useEffect(() => {
    if (open) {
      fetchUsers();
    } else {
      setSelectedUser(null);
      setSelectedCCUsers([]);
      setCuentaGlobal('');
      setCuentasPorDivisa({});
      setMismaCuenta(true);
    }
  }, [open, fetchUsers]);

  const handleSend = async () => {
    if (!selectedUser?.CORREO) {
      handleOpenSweetAlert('El usuario seleccionado no tiene correo válido.', 'warning');
      return;
    }

    setSending(true);
    try {
      // Si es misma cuenta, normalizamos para el envío
      const cuentasEnvio = mismaCuenta 
        ? Object.keys(totalsByCurrency).reduce((acc, curr) => ({ ...acc, [curr]: cuentaGlobal }), {})
        : cuentasPorDivisa;

      await axios.post(`${apiUrl}/tesoreroSolicitarFondosCorreo`, {
        usuarioDestino: selectedUser,
        ccUsers: selectedCCUsers,
        filas: selectedRows,
        totalsByCurrency,
        cuentasPorDivisa: cuentasEnvio,
        loggedInUsername
      });
      handleOpenSweetAlert('Solicitud de fondos enviada correctamente', 'success');
      onClose();
      if (onSent) onSent();
    } catch (err) {
      handleOpenSweetAlert('Error al enviar la solicitud de fondos', 'error');
    } finally {
      setSending(false);
    }
  };

  const parseMonto = (val) => {
    if (typeof val === 'number') return val;
    const num = parseFloat(String(val).replace(/[^0-9.-]+/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const totalsByCurrency = useMemo(() => {
    const totals = {};
    selectedRows.forEach(row => {
      const divisa = (row.DIVISA || 'MXN').toUpperCase();
      const val = parseMonto(row.MONTOSOLICITADO);
      totals[divisa] = (totals[divisa] || 0) + val;
    });
    return totals;
  }, [selectedRows]);

  const formatCurrency = (val, curr = 'MXN') => {
    return new Intl.NumberFormat('es-MX', { 
      style: 'currency', 
      currency: curr || 'MXN' 
    }).format(val);
  };

  const foliosByCurrency = useMemo(() => {
    const groups = {};
    selectedRows.forEach(row => {
      const divisa = (row.DIVISA || 'MXN').toUpperCase();
      if (!groups[divisa]) groups[divisa] = [];
      groups[divisa].push(row.FOLIO);
    });
    return groups;
  }, [selectedRows]);

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Sistema MAVI';
    
    const currencies = Object.keys(totalsByCurrency || {});
    
    for (const curr of currencies) {
      const sheet = workbook.addWorksheet(`Pagos ${curr}`);
      
      // Estilos y Títulos
      sheet.mergeCells('A1:F1');
      const titleCell = sheet.getCell('A1');
      titleCell.value = `REPORTE DE SOLICITUD DE FONDOS (${curr})`;
      titleCell.font = { bold: true, size: 14, color: { argb: 'FF0655A5' } };

      sheet.mergeCells('A2:F2');
      const accCell = sheet.getCell('A2');
      const cuentaAdepositar = mismaCuenta ? cuentaGlobal : (cuentasPorDivisa?.[curr] || 'Sin especificar');
      accCell.value = `BANCO / CUENTA PARA DEPÓSITO: ${cuentaAdepositar || 'Sin especificar'}`;
      accCell.font = { bold: true };

      sheet.addRow([]); // Espacio

      // Cabecera de Tabla
      const headerRow = sheet.addRow(['Folio OC', 'Empresa', 'Proveedor', 'Monto OC', 'Descripción Partida', 'Monto Partida']);
      headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0655A5' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { horizontal: 'center' };
        cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
      });

      // Datos Detallados (Consolidados por Folio)
      const filasCurr = selectedRows.filter(f => (f.DIVISA || 'MXN').toUpperCase() === curr);
      filasCurr.forEach(fila => {
        const partidas = fila.partidas || [];
        const descripcionesConsolidadas = partidas.length > 0 
          ? partidas.map(p => `• ${p.DESCRIPCION_PART || 'Sin descripción'}`).join('\n')
          : 'Sin detalle de partidas';

        const totalPartidas = partidas.reduce((acc, p) => acc + parseFloat(String(p.TOTAL_PART || 0).replace(/[^0-9.-]+/g, '')), 0);
        
        const r = sheet.addRow([
          fila.FOLIO,
          fila.EMPRESA,
          fila.PROVEEDOR,
          parseFloat(String(fila.MONTOSOLICITADO || 0).replace(/[^0-9.-]+/g, '')),
          descripcionesConsolidadas,
          totalPartidas
        ]);

        r.getCell(4).numFmt = '"$"#,##0.00';
        r.getCell(6).numFmt = '"$"#,##0.00';
        r.getCell(5).alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
        
        r.eachCell(c => { 
          c.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} }; 
        });
      });

      // --- FILA DE TOTAL FINAL (DESPUÉS DE LA TABLA) ---
      sheet.addRow([]); // Pequeño espacio
      const totalNum = totalsByCurrency[curr];
      const summaryRow = sheet.addRow(['', '', '', '', 'TOTAL A SOLICITAR:', totalNum]);
      
      summaryRow.getCell(5).font = { bold: true, size: 12 };
      summaryRow.getCell(5).alignment = { horizontal: 'right' };
      
      summaryRow.getCell(6).font = { bold: true, size: 12, color: { argb: 'FF10B981' } };
      summaryRow.getCell(6).numFmt = '"$"#,##0.00';
      summaryRow.getCell(6).border = { 
        top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'double' }, right: { style: 'thin' } 
      };
      // --- FIN TOTAL ---

      // Anchos de columna
      sheet.getColumn(1).width = 12;
      sheet.getColumn(2).width = 25;
      sheet.getColumn(3).width = 35;
      sheet.getColumn(4).width = 15;
      sheet.getColumn(5).width = 60;
      sheet.getColumn(6).width = 15;
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `Previsualizacion_Fondos_${new Date().getTime()}.xlsx`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <Dialog 
        open={open} 
        disableRestoreFocus
        onClose={onClose} 
        maxWidth="sm" 
        fullWidth 
        PaperProps={{ 
          sx: { 
            borderRadius: 3, 
            boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            overflow: 'hidden'
          } 
        }}
      >
        {/* Cabecera mas compacta */}
        <Box sx={{ 
          p: 1.5, 
          px: 2,
          background: 'linear-gradient(135deg, #0655A5 0%, #033C75 100%)', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              width: 32, 
              height: 32, 
              borderRadius: '8px', 
              bgcolor: 'rgba(255,255,255,0.15)', 
              display: 'grid', 
              placeItems: 'center'
            }}>
              <MailIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Solicitar Fondos
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.65rem' }}>
                {selectedRows.length} Documentos seleccionados
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2, bgcolor: '#fcfdfe' }}>
          {/* Seccion de Resumen muy compacta */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.5 }}>
                RESUMEN DE SOLICITUD
              </Typography>
              <Box sx={{ height: '1px', flexGrow: 1, mx: 2, bgcolor: '#eef2f6' }} />
            </Box>
            
            {Object.entries(foliosByCurrency).map(([curr, folios]) => (
              <Box 
                key={curr} 
                sx={{ 
                  mb: 1, 
                  p: 1.5, 
                  borderRadius: 2.5, 
                  bgcolor: '#fff',
                  border: '1px solid #eef2f6',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                  position: 'relative',
                  pl: 2.5,
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    left: 0, top: 10, bottom: 10,
                    width: 4,
                    borderRadius: '0 3px 3px 0',
                    bgcolor: curr === 'USD' ? '#10b981' : '#0655A5'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, color: 'text.primary', minWidth: 40 }}>{curr}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, flexGrow: 1 }}>
                    {folios.slice(0, 15).map((folio, idx) => (
                      <Chip 
                        key={idx} 
                        label={folio} 
                        size="small" 
                        sx={{ 
                          height: 22, 
                          fontSize: '0.7rem', 
                          fontWeight: 700, 
                          bgcolor: '#f1f5f9', 
                          border: '1px solid #e2e8f0',
                          px: 0.5 
                        }} 
                      />
                    ))}
                    {folios.length > 15 && (
                      <Chip 
                        label={`+${folios.length - 15}`} 
                        size="small" 
                        sx={{ 
                          height: 22, 
                          fontSize: '0.7rem', 
                          fontWeight: 700, 
                          bgcolor: '#e2e8f0' 
                        }} 
                      />
                    )}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: curr === 'USD' ? '#059669' : '#0655A5', letterSpacing: -0.5 }}>
                    {formatCurrency(totalsByCurrency[curr], curr)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Seccion de Cuenta de Depósito - DESTACADA */}
          <Box sx={{ 
            p: 2, 
            mb: 2,
            borderRadius: 3, 
            bgcolor: 'rgba(6,85,165,0.04)', 
            border: '1px solid rgba(6,85,165,0.1)' 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceIcon sx={{ fontSize: 16, color: '#0655A5' }} />
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#0655A5', letterSpacing: 0.5 }}>
                  Instrucciones de Pago
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary' }}>
                  ¿Diferente cuenta por divisa?
                </Typography>
                <input 
                  type="checkbox" 
                  checked={!mismaCuenta} 
                  onChange={() => setMismaCuenta(!mismaCuenta)}
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>

            {mismaCuenta ? (
              <TextField
                size="small"
                fullWidth
                label="Cuenta o CLABE para todas las divisas"
                variant="outlined"
                value={cuentaGlobal}
                onChange={(e) => setCuentaGlobal(e.target.value)}
                placeholder="Ej: Banamex 1234... o CLABE..."
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: 2 } }}
              />
            ) : (
              <Stack spacing={1}>
                {Object.keys(foliosByCurrency).map(curr => (
                  <TextField
                    key={curr}
                    size="small"
                    fullWidth
                    label={`Cuenta para depósito en ${curr}`}
                    variant="outlined"
                    value={cuentasPorDivisa[curr] || ''}
                    onChange={(e) => setCuentasPorDivisa({ ...cuentasPorDivisa, [curr]: e.target.value })}
                    placeholder={`Indica la cuenta para ${curr}...`}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: 2 } }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* Seccion de Destinatarios Slim */}
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: 'rgba(6,85,165,0.02)', 
            border: '1px solid rgba(6,85,165,0.08)' 
          }}>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MailIcon sx={{ fontSize: 14, color: '#0655A5' }} />
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#0655A5', letterSpacing: 0.5 }}>
                  DESTINATARIOS
                </Typography>
              </Box>

              <Autocomplete
                size="small"
                options={users}
                loading={loadingUsers}
                value={selectedUser}
                onChange={(e, val) => setSelectedUser(val)}
                getOptionLabel={(opt) => `${opt.USERNAME} (${opt.CORREO || 'Sin correo'})`}
                isOptionEqualToValue={(a, b) => a.USUARIOID === b.USUARIOID}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Tesorero / Finanzas" 
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress color="inherit" size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                multiple
                size="small"
                options={users}
                loading={loadingUsers}
                value={selectedCCUsers}
                onChange={(e, val) => setSelectedCCUsers(val)}
                getOptionLabel={(opt) => `${opt.USERNAME} (${opt.CORREO || 'Sin correo'})`}
                isOptionEqualToValue={(a, b) => a.USUARIOID === b.USUARIOID}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Con Copia (CC)" 
                    variant="outlined"
                    placeholder={selectedCCUsers.length === 0 ? "Opcional..." : ""}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress color="inherit" size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: '#fcfdfe', borderTop: '1px solid #eef2f6', justifyContent: 'space-between' }}>
          <Button 
            onClick={handleDownloadExcel} 
            variant="outlined" 
            color="primary"
            startIcon={<AccountBalanceIcon sx={{ fontSize: 18 }} />}
            sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
          >
            Previsualizar Excel
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} sx={{ fontWeight: 700, textTransform: 'none', color: 'text.secondary' }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSend} 
              variant="contained" 
              disabled={sending || !selectedUser}
              sx={{ 
                bgcolor: '#0655A5', 
                fontWeight: 800, 
                textTransform: 'none', 
                px: 3, 
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(6,85,165,0.25)',
                '&:hover': { bgcolor: '#033C75' }
              }}
            >
              {sending ? <CircularProgress size={20} color="inherit" /> : 'Enviar Solicitud'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <SimpleSuccessSpinner open={sending} handleClose={() => null} message="Enviando correo..." />
    </>
  );
}
